/**
 * Website.jsx
 * What your own sites are doing — one set of figures per app, never a heap.
 *
 * The screen this replaced added every site together: "157 page views, 5
 * downloads" across five products on three sites, with no way to tell whose
 * they were. Three sales of PeekoType in a week when its site had only just
 * started being counted looked, on that screen, exactly like nobody wanting it.
 *
 * So the page is now a card per app, and each card answers the same three
 * questions about that one app: how many took it, how many came, and — when
 * the answer is zero — whether that zero means nobody came or nobody counted.
 *
 * Two things still have to be set up before any figure appears, and the cards
 * say which is missing rather than showing a convincing zero: the site has to
 * be listed, and the counting script has to be on its pages.
 *
 * Nothing here identifies a visitor — no cookies, no addresses — which is why
 * the sites need no consent banner for it.
 *
 * Dependencies: services/api.js, hooks/useBrands.jsx, components/website/*
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { useBrands } from '../hooks/useBrands.jsx';
import { Chart, Check, Warning } from '../components/Icons.jsx';
import { londonDay } from '../utils/time.js';
import AppCard from '../components/website/AppCard.jsx';
import SiteSetup, { Unassigned } from '../components/website/SiteSetup.jsx';
import { Bars, n } from '../components/website/Bars.jsx';

const WINDOWS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

export default function Website() {
  const { brands } = useBrands();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.website.get(days));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  /** Anything that changes the setup: same shape, same handling. */
  async function run(action, after) {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      await action();
      if (after) setNote(after);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /** Copying is the whole point of both the snippet and the tracked address. */
  async function copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      setNote('Copied.');
    } catch {
      setNote('Could not copy — select the text and copy it by hand.');
    }
  }

  if (error && !data) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{error.message}</span>
      </div>
    );
  }

  if (!data) return <p className="mono">Loading…</p>;

  const { sites, snippet, apps, unassigned, totals, from } = data;
  const to = londonDay();

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Website</div>
          <h1>Your apps, one by one</h1>
          <p>
            Every figure below belongs to the app it sits under. No cookies, nothing identifying.
          </p>
        </div>

        <div className="page-head__actions">
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              className={`btn btn--sm ${days === w.days ? 'btn--primary' : ''}`}
              onClick={() => setDays(w.days)}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {/* Side by side, once, so the apps can be compared — then never again,
          because everything after this belongs to exactly one of them. The two
          measures are drawn as two lists rather than one, because downloads and
          page views are different sizes and sharing a scale would make the
          smaller of them look like nothing. */}
      {apps.length > 1 && (
        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Chart size={21} />
            </span>
            <div className="card__head-text">
              <h2>All {apps.length} apps, side by side</h2>
              <p>
                Over the last {days} days: {n(totals.downloads)} downloads and {n(totals.views)} page
                views in total. Each app’s own figures are in its card below.
              </p>
            </div>
          </div>

          <div className="app-columns">
            <div>
              <h3 className="app-sub">Downloads per app</h3>
              <Bars
                rows={apps.map((a) => ({
                  key: a.id,
                  label: a.name,
                  sub: a.host,
                  value: a.totals.downloads,
                  tone: 'downloads',
                }))}
                empty="Nothing downloaded in this window."
              />
            </div>
            <div>
              <h3 className="app-sub">Page views per app</h3>
              <Bars
                rows={apps.map((a) => ({
                  key: a.id,
                  label: a.name,
                  sub: a.host,
                  value: a.totals.views,
                }))}
                empty="No visits in this window."
              />
            </div>
          </div>
        </section>
      )}

      {apps.length === 0 && (
        <section className="card">
          <div className="empty empty--inline">
            <h3>No sites yet</h3>
            <p>Add a site below and paste its one line of script, and its card appears here.</p>
          </div>
        </section>
      )}

      {/* The busiest app is open; the rest are shut but still show their
          figures, so the page opens as a short list rather than a scroll. */}
      {apps.map((app, i) => (
        <AppCard
          key={app.id}
          app={app}
          from={from}
          to={to}
          busy={busy}
          defaultOpen={i === 0}
          onCopy={copy}
          onArchive={(link) => {
            if (
              !window.confirm(
                `Retire "${link.label}"?\n\nThe link stops working, so remove it from your ` +
                  'website first. Its past figures are kept.',
              )
            ) {
              return;
            }
            run(() => api.website.archiveLink(link.id), 'Retired.');
          }}
          onAdd={({ label, url, match, siteId, brandId }) =>
            run(
              () =>
                api.website.addLink({
                  label,
                  url,
                  siteId,
                  brandId: brandId || null,
                  match: match.trim() || null,
                }),
              match.trim()
                ? `Added to ${app.name}. Clicks on that address are counted from now on.`
                : `Added to ${app.name}. Copy its address onto your website’s download button.`,
            )
          }
        />
      ))}

      <Unassigned
        unassigned={unassigned}
        sites={sites}
        busy={busy}
        onAssign={(linkId, siteId) =>
          run(() => api.website.assignLink(linkId, siteId), 'Filed under that app.')
        }
      />

      <SiteSetup
        sites={sites}
        snippet={snippet}
        brands={brands}
        busy={busy}
        onCopy={copy}
        onAdd={(host, brandId) => run(() => api.website.addSite(host, brandId), 'Site added.')}
        onToggle={(s) =>
          run(
            () => api.website.setSiteEnabled(s.id, !s.enabled),
            s.enabled ? 'Paused.' : 'Counting again.',
          )
        }
        onRemove={(s) => {
          if (
            !window.confirm(
              `Remove ${s.host}?\n\nIts visits stop being counted. Figures already collected ` +
                'are kept.',
            )
          ) {
            return;
          }
          run(() => api.website.removeSite(s.id), 'Removed.');
        }}
      />
    </>
  );
}
